<template>
  <PropertyPageHeader>
    <template #headline-prefix>
      <slot name="headline-prefix"></slot>
    </template>
  </PropertyPageHeader>
  <PropertyPageGallery/>
  <PropertyPageNavigation/>

  <div class="property-info">
    <div class="property-info__list pr-6">
      <section
          v-if="hotelProperty?.description"
          class="property-details property-details--description text-body-1"
      >
        <div v-if="!showFullDescription && isClient">
          <span v-html="descriptionHtml.substring(0, fourLinesCharacterLength)"></span><template v-if="!isExpandedDescription">...</template>
        </div>
        <div v-else v-html="descriptionHtml"></div>
        <button
           @click="isExpandedDescription = !isExpandedDescription"
            class="btn btn-link show-more-link"
           aria-label="Toggle full description"
        >
          {{$t('Show')}} {{!isExpandedDescription ? 'More' : 'Less'}}
        </button>
      </section>

      <LoadingOffers />

      <LazyPropertyPageRooms/>

      <LazyPropertyPageVioComparison
        v-if="cheapestRate?.total_price?.converted && date_start && date_end"
        :hotel-id="hotelProperty.id"
        :checkin-date="date_start as string"
        :checkout-date="date_end as string"
        :our-price="Number(cheapestRate?.total_price?.converted?.amount) || undefined"
        :our-currency="cheapestRate?.total_price?.converted?.currency || 'USD'"
      />

      <PropertyPageFacilities class="property-details block-split"/>

      <section id="PropertyPolicies" class="property-details property-details--policies block-split" v-show="property.check_in_time && property.check_out_time">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Policies')}}</h2>
        <div class="container" v-if="property.check_in_time && property.check_out_time">
          <div class="text-title-2 policies-sub-header">{{$t('Check-in')}}</div>
          <div class="text-title-2 policies-sub-body">{{$t('Check-in time starts at')}} {{property.check_in_time}}</div>
          <div class="text-title-2 policies-sub-header pt-4">{{$t('Check-out')}}</div>
          <div class="policies-sub-body">{{$t('Check-out before')}} {{property.check_out_time}}</div>
        </div>
      </section>
      <section class="property-details property-details--important block-split">
        <h2 class="text-title-2 fw-semi-bold policies-sub-header">{{$t('Important information')}}</h2>
        <div class="container policies-sub-body">
          <template v-if="property.keep_in_mind_info?.length">
            <div v-for="(info, idx) in property.keep_in_mind_info" :key="idx" class="important-info-item">
              {{ info.description }}
            </div>
          </template>
          <template v-else>
            {{$t('Please present an ID and a valid credit or debit card for a security deposit and incidentals at check-in time.')}}
            <div class="sub">
              {{$t('Minimum check-in age for the lead traveler varies by hotel.')}}
            </div>
          </template>
        </div>
      </section>
      <section id="PropertyLocation" class="property-details property-details--location block-split">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Location')}}</h2>
        <div class="container">
          <div class="map-container" v-if="hotelProperty.latitude && hotelProperty.longitude">
            <!-- Static map for SSR/crawlers - visible until interactive map loads -->
            <img
              v-if="!isClient"
              :src="`https://staticmap.openstreetmap.de/staticmap.php?center=${hotelProperty.latitude},${hotelProperty.longitude}&zoom=15&size=600x300&maptype=mapnik&markers=${hotelProperty.latitude},${hotelProperty.longitude},red-pushpin`"
              :alt="`Map showing location of ${hotelProperty.name}`"
              width="600"
              height="300"
              class="static-map"
              referrerpolicy="origin"
            />
            <!-- Interactive map for client-side -->
            <client-only>
              <LazyMapView :lat="hotelProperty.latitude" :lon="hotelProperty.longitude" />
            </client-only>
          </div>
          <div class="pt-3 text-body-1 fw-semi-bold">
            {{property.address}}
          </div>
          <!--TODO: we need to add this info on BE-->
          <!-- Uncomment when we have this info -->
          <!--<div class="mt-1 property-location-description">
            The Rum House, featuring a copper and wood themed design, is only a minute away from Edison and offers live entertainment on occasion. The on-site restaurant, Bond 45 serves a vegetable antipasto bar and a menu that includes veal chops and seafood. Friedman's, the restaurant that has "Eat good food" as their mantra, is also open in the Edison.
            Rockefeller Center is 2641 feet from The Edison while the Empire State Building is 1 mi away.  The Jacob K. Javits is 1.2 mi from The Edison Hotel.
          </div>

          <button aria-label="Show on map" class="btn btn-secondary mt-6">
            Show on map
          </button> -->
          <!-- End of missing info comment -->
        </div>
      </section>
      <!--TODO: add FAQ information -->
      <section id="PropertyFAQ" class="property-details block-split">
        <h2 class="text-title-2 fw-semi-bold">FAQ</h2>
        <div class="container">
          <ExpandablePanel
            v-for="(faqItem, index) in faqList"
            :openByDefault="index === 0"
            :key="index"
            :title="faqItem.title"
            :text="faqItem.text"
          />
        </div>
      </section>
      <!--End of FAQ -->
      <section class="property-details block-split">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Guest reviews')}}</h2>
        <div class="container">
          <LazyPropertyPageReviews/>
        </div>
      </section>

      <!-- Similar Properties -->
      <LazyPropertyPageSimilarProperties
        v-if="hotelProperty?.id"
        :currentHotel="hotelProperty"
        :route="route"
        :currentPrice="Number(cheapestRate?.total_price?.converted?.amount) || undefined"
      />

    </div>
    <div class="property-info__booking">
      <PropertyPageBookingBlock ref="bookingBlock" @on-availability-update="onAvailabilityUpdate"/>
    </div>
  </div>

</template>
<script setup lang="ts">
import {usePropertyStore} from '~/store/property'
import ExpandablePanel from '~/components/ExpandablePanel.vue'
import LoadingOffers from "~/components/LoadingOffers.vue";
import { useRoute } from "vue-router";
import {imageUrlWithProxy} from "~/utils/imageproxy";
import dayjs from '~/utils/dayjs';
const runtimeConfig = useRuntimeConfig()
const propertyStore = usePropertyStore()
const emit = defineEmits(['onAvailabilityUpdate'])
const property = usePropertyStore().property
const hotelProperty = computed(() => propertyStore.property)
const bookingBlock = ref()
const isExpandedDescription = ref(false)
const descriptionHtml = computed(() => {
  if (!hotelProperty.value?.description) return ''
  return hotelProperty.value.description.replace(/\n/g, '<br>')
})
let _windowWidth = ref(window?.innerWidth || 0)
const isClient = ref(false);
const faqList = ref<any[]>([])
const config = useRuntimeConfig()
const configData = config.public
const route = useRoute()
const { gtag } = useGtag()

const texts = [{
  brand_name: 'Tripening'
}]

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

function handleResize() {
  _windowWidth.value = window.innerWidth
}

//computed
const siteHost = computed(() => {
  return configData.siteName || ''
})

const cheapestRate = computed(() => {
  return usePropertyStore()?.rooms[0]?.rates[0]
})

const ratesList = computed(() => {
  const rates: any[] = []
  usePropertyStore()?.rooms.forEach((room: any) => {
    room.rates.forEach((rate:any) => {
      rates.push(rate)
    })
  })
  return rates
})

let isMobile = computed(() => _windowWidth.value && _windowWidth.value <= 430)

let showFullDescription = computed(() => {
  const _isExpDescription = isExpandedDescription?.value
  const _descriptionLength = property?.description?.length || 0
  const isShortDescription = _descriptionLength < fourLinesCharacterLength.value
  return (isShortDescription|| _isExpDescription) || !isMobile.value
})

const amenitiesList = computed(() => {
  if (!property.amenities?.length) {
    return []
  }
  const temp = [...property.amenities.filter((amenity: any) => amenity.group === 'hotel')]
  temp.sort((a: any, b: any) => a.code - b.code)
  return temp.splice(0, 11)
})

const fourLinesCharacterLength = computed(() => {
  if (!_windowWidth.value || !isMobile) return 0

  const AVERAGE_FONT_SET_WIDTH_PX = 10; //for font size 16 px
  const screenWidth = _windowWidth.value;
  const averageCharsPerLine = screenWidth / AVERAGE_FONT_SET_WIDTH_PX;
  return averageCharsPerLine * 4;
});

const isDescriptionLessThanFourLines = computed(() => {
  if (!window) return  false

  const AVERAGE_FONT_SET_WIDTH_PX = 10; //for font size 16 px
  const screenWidth = window?.innerWidth;
  const averageCharsPerLine = screenWidth / AVERAGE_FONT_SET_WIDTH_PX;

  return property.description.length < averageCharsPerLine && screenWidth < 768;
  //pause continue here
});

const breadcrumbs =  {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `https://${siteHost.value}`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': property.city || 'City',
      'item': `https://${siteHost.value}${route.fullPath}`,
    },
  ],
}

const date_start = route.query?.['date_start'] || route.query?.['checkin-date']
const date_end = route.query?.['date_end'] || route.query?.['checkout-date']


function checkFreeCancellation(rate: any) {
  if (rate.is_refundable) {
    return rate.cancellation_policies?.some((policy: any) => {
      return (new Date().getTime() - new Date(policy?.dt_deadline).getTime()) < 0
    })
  }
  return false
}


const surchargesSum = (rate: any) => {
  const rateSum = rate?.surcharges?.reduce((acc: any, surcharge: any) => {
    return surcharge.is_mandatory ? acc + parseFloat(surcharge.price.converted.amount) : acc
  }, 0) || 0
  return rateSum.toFixed(2)
}

const taxesSum = (rate: any) => {
  const rateSum = rate?.taxes_and_fees?.reduce((acc: any, tax: any) => {
    return tax.is_mandatory ? acc + parseFloat(tax.price.converted.amount) : acc
  }, 0) || 0

  return rateSum.toFixed(2)
}

// Google's DateTime format is YYYY-MM-DD[THH:mm:ss]; a space separator is not a
// valid DateTime, so the offer's stay window was being discarded.
function jsonldAddress(prop: any) {
  const address = {
    'streetAddress': prop.address || '',
    'addressLocality': prop.city_name || prop.geo_place?.name || '',
    'addressRegion': prop.region || prop.geo_place?.state_name || '',
    'postalCode': prop.postal_code || '',
    'addressCountry': prop.country_code || prop.geo_place?.country_code || '',
  }
  return Object.values(address).every(Boolean) ? address : null
}

function jsonldStayDateTime(date: any, clockTime: string) {
  const day = dayjs(String(date))
  return day.isValid() ? `${day.format('YYYY-MM-DD')}T${clockTime}` : ''
}

// Google matches the rate it advertised against the landing page and expects to
// find it listed first. Ours came out in whatever order the supplier returned.
const googleDisplayedTotal = computed(() => {
  const price = parseFloat(route.query?.['google-displayed-price'] as string)
  if (!Number.isFinite(price) || price <= 0) return 0
  return price + (parseFloat(route.query?.['google-displayed-tax'] as string) || 0)
})

function isGoogleDisplayedRate(rate: any) {
  const googleTotal = googleDisplayedTotal.value
  if (!googleTotal) return false
  const ourTotal = parseFloat(rate?.total_price?.converted?.amount ?? '')
  return Number.isFinite(ourTotal) && Math.abs(ourTotal - googleTotal) / googleTotal < 0.03
}

// `cover_image` is an object and `images` is keyed by category, so
// `cover_image_url` / `images[0]` were always undefined and every hotel page
// shipped the fallback: "www.tripening.com/img/no-photo-big.svg.svg" — no
// scheme, doubled extension, and no such file.
function resolvePropertyPhoto(prop: any) {
  const gallery = Array.isArray(prop?.images) ? prop.images : Object.values(prop?.images || {}).flat()
  const first: any = gallery[0]
  const source = prop?.cover_image?.url
      || prop?.cover_image_url
      || (typeof first === 'string' ? first : first?.url)
  return source ? imageUrlWithProxy(source, 450, 300) : ''
}

function setJsonld() {
  const prop = propertyStore.property
  if (!prop?.id) {
    return null;
  }

  let _amenities: any[] = [];

  prop.hotel_amenities?.forEach((amenity: any) => {
    _amenities.push({ '@type': 'LocationFeatureSpecification', 'value': true, 'name': amenity.name });
  });

  const checkInTime = prop.check_in_time
    ? dayjs(prop.check_in_time, 'hh:mm A').format('HH:mm:ss')
    : '15:00:00'
  const checkOutTime = prop.check_out_time
    ? dayjs(prop.check_out_time, 'hh:mm A').format('HH:mm:ss')
    : '11:00:00'

  // Build containsPlace from rooms — Google requires Hotel.containsPlace → HotelRoom.offers
  // (see https://developers.google.com/hotels/hotel-prices/structured-data/hotel-price-structured-data)
  const containsPlace: any[] = []
  const rooms = propertyStore.rooms
  if (rooms?.length) {
    // Every rate the page sells, not just rates[0]: the rest of the ladder stayed
    // visible to guests while being invisible to the crawler, and Google requires
    // the two to agree. Google's rate leads, both within a room and across rooms.
    const orderedRooms = [...rooms]
        .map((room: any) => ({
          room,
          rates: [...(room.rates || [])].sort((a: any, b: any) =>
              Number(isGoogleDisplayedRate(b)) - Number(isGoogleDisplayedRate(a))),
        }))
        .filter((entry: any) => entry.rates.length)
    orderedRooms.sort((a: any, b: any) =>
        Number(isGoogleDisplayedRate(b.rates[0])) - Number(isGoogleDisplayedRate(a.rates[0])))

    // One Offer per rate, all hanging off the single HotelRoom node for that room.
    const buildOffer = (rate: any) => {
      const currency = rate.total_price?.converted?.currency || 'USD'

      const priceComponents: any[] = [
        {
          '@type': 'UnitPriceSpecification',
          'name': 'Base rate',
          'price': parseFloat(rate.base_price?.converted?.amount) || 0,
          'priceCurrency': currency,
        },
      ]

      // Mandatory surcharges (the resort fee) live on rate.surcharges, NOT rate.taxes_and_fees.
      // They were omitted here, so priceComponent never summed to the declared price — a
      // structured-data inconsistency for the crawler and an incomplete breakdown for the user.
      // total_price already includes them, so this adds the missing components only; it does
      // not change the headline price.
      const mandatoryCharges = [
        ...(rate.taxes_and_fees || []),
        ...(rate.surcharges || []),
      ]

      mandatoryCharges
        ?.filter((fee: any) => fee.price?.converted?.amount > 0 && fee.is_mandatory)
        .forEach((fee: any) => {
          priceComponents.push({
            '@type': 'UnitPriceSpecification',
            'name': fee.name || (fee.is_tax ? 'GenericTax' : 'ResortFee'),
            'priceComponentType': fee.is_tax ? 'GenericTax' : 'ResortFee',
            'price': parseFloat(fee.price.converted.amount) || 0,
            'priceCurrency': fee.price.converted.currency || currency,
          })
        })

      const isRefundable = checkFreeCancellation(rate)

      return {
        '@type': ['Offer', 'LodgingReservation'],
        ...(date_start && { 'checkinTime': jsonldStayDateTime(date_start, checkInTime) }),
        ...(date_end && { 'checkoutTime': jsonldStayDateTime(date_end, checkOutTime) }),
        'priceSpecification': {
          '@type': 'CompoundPriceSpecification',
          'price': parseFloat(rate.total_price?.converted?.amount) || 0,
          'priceCurrency': currency,
          'priceComponent': priceComponents,
        },
        ...(isRefundable && {
          'hasMerchantReturnPolicy': {
            '@type': 'MerchantReturnPolicy',
            'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
            'refundType': 'https://schema.org/FullRefund',
            'merchantReturnDays': dayjs(rate.cancellation_policies?.[0]?.dt_deadline).format('YYYY-MM-DDTHH:mm:ss'),
            'restockingFee': 0,
          },
        }),
      }
    }

    orderedRooms.forEach(({ room, rates }: any) => {
      const rate = rates[0]

      let roomAmenities: any[] = []
      room.amenities?.forEach((amenity: any) => {
        roomAmenities.push({ '@type': 'LocationFeatureSpecification', 'value': true, 'name': amenity.name })
      })

      containsPlace.push({
        '@type': ['HotelRoom', 'Product'],
        'identifier': String(room.id),
        'name': room?.room_category || room?.description || 'Hotel Room',
        ...(room.description && { 'description': room.description }),
        ...(room?.images?.[0]?.url && {
          'image': imageUrlWithProxy(room.images[0].url, 450, 300),
        }),
        ...(room.beds?.length > 0 && {
          'bed': {
            '@type': 'BedDetails',
            'numberOfBeds': room.beds.length,
          },
        }),
        'occupancy': {
          '@type': 'QuantitativeValue',
          'value': room.max_occupancy || rate.max_occupancy || 2,
        },
        ...(roomAmenities.length > 0 && { 'amenityFeature': roomAmenities }),
        'offers': rates.map(buildOffer),
      })
    })
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    'url': `https://${siteHost.value}${route.fullPath}`,
    'name': prop.name,
    'description': prop.short_description || prop.description,
    "identifier": String(prop.id),
    // Address is optional for Google, but once present every PostalAddress
    // subfield is mandatory — half an address invalidates the node.
    ...(jsonldAddress(prop) && { 'address': { '@type': 'PostalAddress', ...jsonldAddress(prop) } }),
    ...(resolvePropertyPhoto(prop) && { 'photo': resolvePropertyPhoto(prop) }),
    ...(prop.check_in_time && { 'checkinTime': prop.check_in_time }),
    ...(prop.check_out_time && { 'checkoutTime': prop.check_out_time }),
    ...(prop.avg_rating > 0 && prop.avg_rating <= 5 && {
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': prop.avg_rating,
        'reviewCount': prop.reviews_count,
      },
    }),
    ...(prop.rating > 0 && prop.rating <= 5 && {
      'starRating': {
        '@type': 'Rating',
        'ratingValue': prop.rating,
      },
    }),
    ...(_amenities.length > 0 && { 'amenityFeature': _amenities }),
    ...(containsPlace.length > 0 && { 'containsPlace': containsPlace }),
  }
}

interface FaqItem {
  title: string;
  text: string;
}

//methods
function onAvailabilityUpdate() {
  emit('onAvailabilityUpdate')
}

function createFAQList() {
  //create FAQList
  const items: any[] = []
  items.push(createFaqItem(hotelProperty.value, "Where is {hotel_name} located?", 'Located in ' + hotelProperty.value.address))
  if (isPetsFriendly()) {
    items.push(createFaqItem(hotelProperty.value, "Is {hotel_name} pet-friendly?", "Yes, {hotel_name} is pet-friendly."))
  }

  if (property?.location_info) {
    items.push(createFaqItem(hotelProperty.value, "How many rooms does {hotel_name} have?", `{hotel_name} has ${hotelProperty.value?.location_info.rooms} rooms.`))
    //How many floors does Hotel Edison have?
    items.push(createFaqItem(hotelProperty.value, "How many floors does {hotel_name} have?", `{hotel_name} has ${hotelProperty.value?.location_info.floors} floors.`))
  }

  if (property.payment_method) {
    //What payment cards are accepted at Hotel Edison?
    items.push(createFaqItem(hotelProperty.value, "What payment cards are accepted at {hotel_name}?", `The following payment methods are accepted at {hotel_name}: ${setAvailablePaymentMethodsString()}.`))
  }

//faqItem for fitness center
  if (property.sport_facilities) {
    const regex = /fitness/i;
    const facility = property.sport_facilities.find((facility: any) => facility.code === 0 || regex.test(facility.description));
    if (facility) {
      items.push(createFaqItem(hotelProperty.value, "Does {hotel_name} have a fitness center?", "Yes, {hotel_name} has a fitness center."));
    }
  }

  faqList.value = items
}

function setAvailablePaymentMethodsString() {
  const PATMENT_METHODS_NAMES: any = {
    "american_express": 'American Express',
    "diners_club": 'Diners Club',
    "ec": 'EC-Card',
    "eurocard": 'Eurocard',
    "euro_600": 'Euro6000',
    "jcb": 'JCB',
    "mastercard": 'Mastercard',
    "maestro": 'Maestro',
    "visa": 'Visa',
    "visa_electron": 'Visa Electron',
  }

  const allPaymentMethods =  Object.keys(hotelProperty.value.payment_method || {})
  const allAvailablePaymentMethods = allPaymentMethods.filter((m: any) => hotelProperty.value.payment_method[m])
  const allAvailablePaymentMethodsNames = allAvailablePaymentMethods.map((m: any) => PATMENT_METHODS_NAMES[m])

  return allAvailablePaymentMethodsNames.join(', ')
}

//create FAQ Item
function isPetsFriendly() {
  const regex = /pets/i

  return checkStringInAmenities(regex)
}

function checkStringInAmenities(regex: any) {
  return amenitiesList?.value.some((amenity: any) =>
      Object.values(amenity).some((value: any) =>
          typeof value === 'string' && regex.test(value)
      )
  )
}

function createFaqItem(hotel: any, template: any, textTemplate: any): FaqItem {
  let title = '',
      text = ''
  const hotelName = hotel.name;
  const regex = /\{hotel_name\}/;

  title = template.replace(regex, hotelName);
  text = textTemplate.replace(regex, hotelName);

  return {title, text}
}

watch(hotelProperty, () => {
  createFAQList()
})

// Generate FAQ schema for structured data
function getFaqSchema() {
  if (!faqList.value || faqList.value.length === 0) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqList.value.map((item: FaqItem) => ({
      '@type': 'Question',
      'name': item.title,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.text
      }
    }))
  }
}

//add event listener to window resize
onMounted(() => {
  gtag('event', 'page_view', {
    affiliation: 'Tripening',
    items: [
      {
        item_name: hotelProperty.value.name || '',
        item_id: hotelProperty.value.id || '',
      }
    ]
  })
  isClient.value = true;
  window.addEventListener('resize', handleResize)
  createFAQList()


})
// Memoize JSON-LD to prevent re-evaluation loops on deep object recreation
const hotelJsonld = computed(() => setJsonld())
const faqJsonld = computed(() => getFaqSchema())
useJsonld(() => hotelJsonld.value as any)
useJsonld(() => ({ ...breadcrumbs} as any))
useJsonld(() => faqJsonld.value as any)

useHead({
  title: `${hotelProperty.value?.name || ''} - Best Rates on ${runtimeConfig.public.brand}.com`,
  link: [
    {
      rel: 'canonical',
      href: `${runtimeConfig.public.baseURL}/hotels/${hotelProperty.value?.geo_place?.slug || 'results'}/${hotelProperty.value.id}-${hotelProperty.value.slug}`
    },
  ],
  meta: [
    {
      'property': 'og:title',
      'content': `${hotelProperty.value?.name || ''}`,
    },
    {
      'property': 'og:description',
      'content': `Book your stay at ${hotelProperty.value?.name || ''} with exclusive rates. Save up to 10% on ${runtimeConfig.public.brand}.com. No hidden fees, best price guaranteed.`,
    },
    {
      'property': 'og:url',
      'content': `${runtimeConfig.public.baseURL}${route.fullPath}`,
    },
    {
      'property': 'og:image',
      'content': `${hotelProperty.value?.cover_image?.url || ''}`,
    },
    {
      'property': 'twitter:card',
      'name': 'twitter:card',
      'content': 'summary_large_image',
    },
    {
      'property': 'twitter:text:title',
      'name': 'twitter:text:title',
      'content': `${hotelProperty.value?.name || ''} - Best Rates on ${runtimeConfig.public.brand}.com`,
    },
    {
      'name': 'description',
      'content': `Book your stay at ${hotelProperty.value?.name || ''} with exclusive rates. Save up to 10% on ${runtimeConfig.public.brand}.com. No hidden fees, best price guaranteed.`,
    }
  ],
})
</script>

<style lang="scss">
.property-info {
  display: flex;
  max-width: 100%;

  //tablet (two-column mode)
  @media screen and (max-width: 1024px) and (min-width: 769px){
    justify-content: space-between;
  }

  .property-info__list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 28px;
    min-width: 0; // Allow flex item to shrink below content width
    overflow: hidden; // Contain horizontal overflow from carousels

    @media screen and (max-width: 1024px) and (min-width: 769px) {
      max-width: 608px;
    }

    &.pr-6 {
      @media screen and (max-width: 1024px) and (min-width: 769px) {
        padding-right: 0;
      }

    }

    .property-details {
      padding-bottom: 32px;
      //border-bottom: 1px solid var(--grey);

      @media screen and (max-width: 1024px) {
        padding-bottom: 6px;
        border-bottom: none;
      }

      &.block-split {
        display: flex;
        gap: 24px;
        align-items: flex-start;

        //vertical tablet
        @media screen and (max-width: 768px) and (min-width: 430px){
          flex-direction: column;
        }

        & > h2 {
          width: 167px;
          min-width: 167px;

          @media screen and (max-width: 1024px) and (min-width: 769px){
            width: auto;
            max-width: 132px;
            min-width: 132px;
          }

          @media screen and (max-width: 768px) and (min-width: 430px) {
            max-width: 100%;
          }
        }
        .container {
          flex: 1;
        }
      }
      &.pt-8 {

        //vertical tablet
        @media screen and (max-width: 768px) and (min-width: 431px){
          padding-top: 37px;
        }

        @media screen and (max-width: 430px) {
          padding-top: 24px;
        }
      }

      &--rooms {
        padding-top: 0;
        position: relative;

        .p-dialog-mask {
          position: absolute !important;
          align-items: flex-start !important;
        }

        &.block-split {
          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 430px){
            gap: 1px;
          }
        }
      }

      &--description {
        padding-top: 24px;

        @media screen and (max-width: 768px) {
          padding-bottom: 0;
          border-bottom: none;
        }
      }

      &--policies {

        &.block-split {
          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 430px){
            gap: 0;
          }

          h2.text-title-2 {
            @media screen and (max-width: 768px) {
              padding-bottom: 10px;
            }
          }

          .pt-1, .pt-5 {
            @media screen and (max-width: 768px) {
              padding-top: 0;
            }
          }
        }

        .policies-sub-header {
          font-size: 16px;
          color: var(--black);

          //tablet
          @media screen and (max-width: 1024px) and (min-width: 769px){
            font-size: 18px;
          }

          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 431px){
            font-size: 16px;
          }

          @media screen and (max-width: 430px) {
            font-size: 16px;
            text-transform: capitalize;
          }

          &.pt-5 {
            //tablet
            @media screen and (max-width: 1024px) and (min-width: 768px){
              padding-top: 0.7rem;
            }

            //vertical tablet
            @media screen and (max-width: 768px) and (min-width: 431px){
              padding-top: .2rem;
            }
          }

          & + .pt-1 {
            @media screen and (max-width: 1024px) and (min-width: 768px){
              padding-top: 0;
            }
          }
        }

        .policies-sub-body {
          color: var(--dark-grey);
          font-size: 14px;
          font-weight: 400;
          line-height: 142.857% ;
        }
      }

      &--location {
        &.block-split {

          //vertical tablet
          @media screen and (max-width: 976px) and (min-width: 340px){
            gap: 12px;
          }

          h2.text-title-2 {
            padding-bottom: 12px;
            //vertical tablet
            @media screen and (max-width: 768px) and (min-width: 430px){
              padding-bottom: 0;
            }
          }

          .btn {
            //mobile
            @media screen and (max-width: 430px) {
              width: 100%;
            }
          }

          .property-location-description {
            color: var(--dark-grey);
            font-size: 14px;
            font-weight: 400;
            line-height: 142.857%
          }
        }
      }

      &--important {
        &.block-split {
          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 430px){
            gap: 13px;
          }
        }

        .important-info-item {
          padding: 4px 0;

          &:not(:last-child) {
            border-bottom: 1px solid var(--grey-border);
            margin-bottom: 4px;
          }
        }
      }

      .show-more-link {
        margin-top: 10px;
        display: none;

        @media screen and (max-width: 430px) {
          display: block;
        }
      }
    }
  }
  .property-info__booking {
    width: 357px;
    position: relative;
  }
  @media screen and (max-width: 1024px) and (min-width: 769px){
    .property-info__booking {
      max-width: 292px;
      position: relative;
    }
  }

  // Single-column layout for tablets and phones
  @media screen and (max-width: 768px) {
    display: block;
    .property-info__booking {
      position: fixed;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      z-index: 2;
    }
    .property-info__list {
      padding-right: 0;
      .property-details {
        &.block-split {
          display: block;
          h2 {
            padding-bottom: 20px;
            width: 100%;
            max-width: 100%;
          }
        }
      }
    }
  }
}
.map-container {
  width: 100%;
  position: relative;
  z-index: 1;
  img {
    border-radius: 8px;
  }
  .static-map {
    width: 100%;
    height: auto;
    max-width: 600px;
  }
}
</style>
